import {Fragment, useState} from "react";
import {format} from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {faEye, faEdit, faTrash} from "@fortawesome/free-solid-svg-icons";
import ArticleAPI from '~/api/articleApi';
import CommonAPI from '~/api/commonApi';
import {Controller, useForm} from "react-hook-form";
import {
    BreadCrumb,
    TablePaging,
    CenteredModal,
    Editor,
    FormInput,
    DropzoneFile,
    DropdownAction,
    ReactSingleSelect
} from '~/components';
import {toast} from "react-toastify";
import commonApi from "~/api/commonApi";
import {ClockPlus, Plus} from "lucide-react";

const breadCrumbItem = [
    { text: "Landing Page", to: "/ai-chatbot/administrator", isActive: false },
    { text: "Article", to: "", isActive: true },
]

function Article(){
    const [reloadTable, setReloadTable] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isAddNew, setIsAddNew] = useState(false);
    const [articleDetail, setArticleDetail] = useState({});
    const [selectedFile, setSelectedFile] = useState(null);
    const [content, setContent] = useState("");
    const [isViewMode, setIsViewMode] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [id, setId] = useState(0);

    const handleAddNewArticle = () => {
        setIsViewMode(false);
        ArticleAPI.getById("0")
            .then(res => {
                setArticleDetail(res.data);
                setIsAddNew(true);
                setShowModal(true);
            })
            .catch(err => {  });
    }

    const tablePagingConfig = {
        columns: [
            {
                name: 'ID',
                width: '70px',
                className: 'fw-bold',
                key: 'ArticleId'
            },
            {
                name: 'TITLE',
                width: '200px',
                key: 'Title'
            },
            {
                name: 'ROUTE PATH',
                width: '200px',
                key: 'RoutePath'
            },
            {
                name: 'DESCRIPTION',
                width: '300px',
                key: 'Description',
                isMoreLess: true
            },
            {
                name: 'TYPE',
                width: '100px',
                key: 'ArticleTypeName'
            },
            {
                name: 'MODIFIED BY',
                width: '150px',
                key: 'ModifiedBy'
            },
            {
                name: 'MODIFIED DATE',
                width: '150px',
                key: 'ModifiedDate',
                template: (row) => {
                    return (
                        <span>{format(new Date(row.ModifiedDate), "dd-MM-yyyy")}</span>
                    )
                }
            },
            {
                name: 'ACTION',
                width: '70px',
                className: 'text-center',
                headerClassName: 'text-center',
                key: 'Action',
                template: (row) => {
                    return (
                        <div className="dropdown d-inline-block">
                            <DropdownAction dropdownItems={[
                                { text: "View Details", icon: faEye, onClick: () => handleEditArticle(row.ArticleId, true) },
                                { text: "Edit", icon: faEdit, onClick: () => handleEditArticle(row.ArticleId, false) },
                                {text: "Delete", icon: faTrash, onClick: () => {
                                        setId(row.ArticleId);
                                        setShowDeleteModal(true);
                                }}
                            ]} isVerticalIcon={false}/>
                        </div>
                    )
                }
            }
        ],
        url: "/articles/paged",
        tableKey: "TableArticle",
        extraButtons: [
            <button className="btn btn-bt-primary ms-4" type="button" onClick={handleAddNewArticle}>Add New Article
                <Plus size={15}/>
            </button>
        ]
    }

    const {
        register,
        handleSubmit,
        reset,
        control,
        formState: { errors }
    } = useForm();

    const handleEditArticle = (id, viewMode) => {
        setIsViewMode(viewMode);
        ArticleAPI.getById(id)
            .then(res => {
                const resultDetails = res.data?.Article;
                if (resultDetails) {
                    commonApi.getUploadFile(resultDetails.ThumbnailFileName)
                        .then((res) => {
                            const file = new File([res.data], resultDetails.ThumbnailFileName, { type: res.data.type });
                            const preview = URL.createObjectURL(file);
                            const newFile = {
                                name: file.name,
                                size: (file.size / 1024).toFixed(2) + " KB",
                                preview,
                                file,
                            };
                            setSelectedFile(newFile);
                        })
                        .catch(() => {
                        });
                }
                setArticleDetail(res.data);
                const formValues = { ...resultDetails };
                formValues.TypeId = resultDetails.ArticleTypeId
                    ? { value: resultDetails.ArticleTypeId, label: resultDetails.ArticleTypeName}
                    : null;
                reset(formValues);
                setIsAddNew(false);
                setShowModal(true);
            })
            .catch(err => {  });
    }

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedFile(null);
        reset({
            Title: "",
            Content: "",
            Description: "",
            RoutePath: "",
            file: null,
            TypeId: null
        })
    }

    const onSubmitFrom = (data) => {
        const { file, TypeId, ArticleTypeId, ...restData } = data;
        const formData = new FormData();
        Object.entries(restData).forEach(([key, value]) => {
            formData.append(key, value);
        });
        formData.append("ArticleTypeId", TypeId.value);
        if (file?.[0]) {
            formData.append("file", file[0]);
        }
        if(!isAddNew){
            ArticleAPI.update(formData)
                .then(res => {
                    res.data?.Success ?
                        toast.success(<span className="fs-9 fw-bold">Your article has been updated.</span>)
                        : toast.error(<span className="fs-9 fw-bold">An error occurred while updating your article.</span>)
                })
                .catch(err => {
                    toast.error(<span className="fs-9 fw-bold">An error occurred while updating your article.</span>)

                })
                .finally(() => {
                    handleCloseModal();
                    setReloadTable(pre => !pre);
                })
        }
        else{
            ArticleAPI.insert(formData)
                .then(res => {
                    res.data?.Success ?
                        toast.success(<span className="fs-9 fw-bold">Your article has been added.</span>)
                        : toast.error(<span className="fs-9 fw-bold">An error occurred while adding your article.</span>)
                })
                .catch(err => {
                    toast.error(<span className="fs-9 fw-bold">An error occurred while adding your article.</span>)

                })
                .finally(() => {
                    handleCloseModal();
                    setReloadTable(pre => !pre);
                })
        }
    }

    const handleConfirmDelete = () => {
        ArticleAPI.delete(id)
            .then(res => {
                res.data?.Success ?
                    toast.success(<span className="fs-9 fw-bold">Your article has been deleted.</span>)
                    : toast.error(<span
                        className="fs-9 fw-bold">An error occurred while deleting your article.</span>)
            })
            .catch(err => {
                toast.error(<span
                    className="fs-9 fw-bold">An error occurred while deleting your article.</span>)
                
            })
            .finally(() => {
                setShowDeleteModal(false);
                setReloadTable(pre => !pre);
            })
    }

    return (
        <Fragment>
            <BreadCrumb breadCrumbItem={breadCrumbItem}/>
            <h2 className="text-bold text-body-emphasis mb-5">Articles</h2>
            <TablePaging tablePagingConfig={tablePagingConfig} reloadTable={reloadTable}/>
            <CenteredModal show={showModal}
                           onClose={handleCloseModal}
                           title={isViewMode ? "Article Details" : (isAddNew ? "Add Article" : "Edit Article")}
                           modalType="modal-lg"
                           formId="formArticleAction"
                           isViewMode={isViewMode}
                           closeText={isViewMode ? "Close" : "Cancel"}
            >
                <div className="mb-3">
                    <form onSubmit={handleSubmit(onSubmitFrom)} id="formArticleAction">
                        <div className="mb-3">
                            <span className="form-label">Type</span>
                            <Controller
                                name="TypeId"
                                control={control}
                                rules={{
                                    required: 'Article type is required.'
                                }}
                                render={({ field }) => (
                                    <ReactSingleSelect
                                        options={articleDetail.ArticleType}
                                        value={field.value}
                                        onChange={field.onChange}
                                        errors={errors}
                                        name="TypeId"
                                        placeholder="Select type..."
                                        disabled={isViewMode}
                                    />
                                )}
                            />
                        </div>
                        <div className="mb-3">
                            <FormInput showHeaderLabel={true}
                                       name = "Title"
                                       errors={errors}
                                       required={true}
                                       label="Assign Title"
                                       validation={{...register("Title", {
                                               required: "Title is required."
                                           })}}
                                       disabled={isViewMode}
                            />
                        </div>
                        <div className="mb-3">
                            <FormInput showHeaderLabel={true}
                                       name = "Description"
                                       textarea={true}
                                       errors={errors}
                                       required={true}
                                       label="Assign Description"
                                       validation={{...register("Description", {
                                               required: "Description is required."
                                           })}}
                                       disabled={isViewMode}
                            />
                        </div>
                        <div className="mb-3">
                            <FormInput showHeaderLabel={true}
                                       name = "RoutePath"
                                       errors={errors}
                                       required={true}
                                       label="Assign Route Path"
                                       validation={{...register("RoutePath", {
                                               required: "Route Path is required."
                                           })}}
                                       disabled={isViewMode}
                            />
                        </div>
                        <div className="mb-3">
                            <DropzoneFile
                                label="Assign Thumbnail(*)"
                                register={register}
                                name="file"
                                errors={errors}
                                selectedFile={selectedFile}
                                setSelectedFile={setSelectedFile}
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="mb-3">
                            <h6 className="mb-2">Assign Content(*)</h6>
                            <Controller
                                name="Content"
                                control={control}
                                rules = {{
                                    validate: value => {
                                        const  stripped = value.replace(/<[^>]+>/g, "").trim();
                                        return stripped.length > 0 || "Content is required.";
                                    }
                                }}
                                render={({ field }) => (
                                    <Editor
                                        value={field.value}
                                        onChange={field.onChange}
                                        errors={errors}
                                        name = "Content"
                                        disabled={isViewMode}
                                    />
                                )}
                            />
                        </div>
                    </form>
                </div>
            </CenteredModal>
            <CenteredModal show={showDeleteModal}
                           onClose={()=> setShowDeleteModal(false)}
                           onConfirm={handleConfirmDelete}
                           title="Delete Article"
            >
                <div className="text-center">
                    <span className="fs-8-1 fw-semibold">Are you sure you want to delete this article?</span>
                </div>
            </CenteredModal>
        </Fragment>
    )
}

export default Article;